/**
 * The agent's cursor on the browser live view.
 *
 * Ported from t3code (`apps/web/src/components/preview/AgentBrowserCursor.tsx`,
 * MIT, pin `e3e7cc3f`). Adaptations: forge's runtime does not echo pointer
 * coordinates, so the cursor is fed from the thread's browser stream activity
 * and holds the fractional position of the last event; positioning is
 * therefore percentage-based against the live surface instead of scaled
 * webview math. Opacity comes from forge's ported
 * `agentBrowserCursorLogic`.
 *
 * @module AgentBrowserCursor
 */
import type { ScopedThreadRef } from "@cmmd-forge/contracts";
import { MousePointer2 } from "lucide-react";
import { useEffect, useState } from "react";

import { selectBrowserThreadState, useBrowserStateStore } from "~/browserStateStore";

import {
  browserControllerFromActivity,
  browserPointerEventFromActivity,
  useBrowserPointerStore,
  type BrowserAgentPointerEvent,
  type BrowserController,
} from "./browserPointerStore";
import { agentBrowserCursorOpacity } from "./agentBrowserCursorLogic";

const CURSOR_ACTIVE_MS = 700;

export interface BrowserAgentCursorLayerProps {
  readonly threadRef: ScopedThreadRef;
  readonly tabId: string | null;
}

/**
 * Feeds the cursor from the thread's browser activity and mounts it over the
 * live surface. Renders nothing until the agent or the human has acted on
 * the tab in this session.
 */
export function BrowserAgentCursorLayer({ threadRef, tabId }: BrowserAgentCursorLayerProps) {
  const threadState = useBrowserStateStore((state) =>
    selectBrowserThreadState(state.browserStateByThreadKey, threadRef),
  );
  const activity = tabId !== null ? (threadState?.lastActivityByTabId[tabId] ?? null) : null;
  const event = useBrowserPointerStore((state) => (tabId ? (state.byTabId[tabId] ?? null) : null));

  useEffect(() => {
    if (!tabId || !activity) return;
    const store = useBrowserPointerStore.getState();
    store.apply(
      browserPointerEventFromActivity({
        tabId,
        activity,
        previous: store.byTabId[tabId] ?? null,
      }),
    );
  }, [activity, tabId]);

  if (!event) return null;
  const controller = browserControllerFromActivity(activity);
  return <AgentBrowserCursor event={event} controller={controller} />;
}

function AgentBrowserCursor({
  event,
  controller,
}: {
  readonly event: BrowserAgentPointerEvent;
  readonly controller: BrowserController;
}) {
  const [inactiveSequence, setInactiveSequence] = useState<number | null>(null);
  const active = inactiveSequence !== event.sequence;

  useEffect(() => {
    const timeout = window.setTimeout(() => setInactiveSequence(event.sequence), CURSOR_ACTIVE_MS);
    return () => window.clearTimeout(timeout);
  }, [event.sequence]);

  return (
    <div
      className="pointer-events-none absolute left-0 top-0 z-40 transition-[opacity] duration-150 ease-out motion-reduce:transition-none"
      style={{
        opacity: agentBrowserCursorOpacity(active, controller),
        left: `${event.x * 100}%`,
        top: `${event.y * 100}%`,
      }}
      aria-hidden="true"
      data-agent-browser-cursor={event.tabId}
    >
      {event.phase === "click" ? (
        <span
          key={event.sequence}
          className="absolute left-0.5 top-0.5 size-4 motion-safe:animate-status-ping rounded-full bg-primary/25 motion-reduce:animate-none"
        />
      ) : null}
      <MousePointer2
        className="relative size-5 -translate-x-0.5 -translate-y-0.5 fill-background text-primary drop-shadow-sm"
        strokeWidth={2}
      />
    </div>
  );
}
